import { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'
import { useQuery } from '@tanstack/react-query'
import { useMutation } from '@tanstack/react-query'
import { useNavigate, useParams } from 'react-router-dom'
import { Button, Col, Divider, Row, Table, Typography, Card, Statistic, Spin, Switch, message } from 'antd'
import moment from 'moment/moment'
import classNames from 'classnames'

import { getCustomer, setCustomer, updateCustomer } from '../../../redux/actions/customers'
import { getUserTransactions, getUserWallet } from '../../../api/wallet-transactions'
import { syncUserWalletSap } from '../../../api/wallet-transactions'
import { getUserLoyaltyBalance, updateUserLoyaltyStatus, getUserLoyaltyHistory, getUserReferralHistory } from '../../../api/loyalty'

import Page from '../../../components/Page'
import SortableColumnTitle from '../../../components/SortableColumnTitle'
import CustomerForm from '../../../components/forms/CustomerForm'
import EditWalletModal from '../../../components/Modal/modals/EditWalletModal/EditWalletModal'
import { syncCustomerSap, syncCustomerBookletCoupons } from '../../../redux/actions/customers'

import styles from './CustomersView.module.scss'

const { Title } = Typography

const ACTIVE_BUTTON = {
  ALL: 'ALL',
  WITHDRAW: 'WITHDRAW',
  DEPOSIT: 'DEPOSIT',
}

const CustomersView = () => {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const { t } = useTranslation()
  const { id } = useParams()
  const [activeButton, setActiveButton] = useState(ACTIVE_BUTTON.ALL)
  const [isModalVisible, setIsModalVisible] = useState(false)
  const { item: customer, isGetLoading, isChangeLoading } = useSelector((state) => state.customers)
  const [pageSize, setPageSize] = useState(10)
  const [currentPage, setCurrentPage] = useState(1)
  const [sortType, setSortType] = useState('createdAt')
  const [sortOrder, setSortOrder] = useState('descend')
  const [transactionType, setTransactionType] = useState(ACTIVE_BUTTON.ALL)
  const [loyaltyHistoryPage, setLoyaltyHistoryPage] = useState(1)
  const [loyaltyHistoryPageSize, setLoyaltyHistoryPageSize] = useState(10)
  const [referralHistoryPage, setReferralHistoryPage] = useState(1)
  const [referralHistoryPageSize, setReferralHistoryPageSize] = useState(10)

  const {
    data: transactions,
    isFetching,
    refetch: refetchTransactions,
    isError,
  } = useQuery(
    ['wallet-transactions', currentPage, pageSize, sortType, sortOrder, transactionType],
    async () =>
      await getUserTransactions(id, {
        currentPage,
        pageSize,
        sortType,
        sortOrder,
        transactionType,
      }),
  )

  const {
    data: wallet,
    isFetching: isWalletFetching,
    refetch: refetchWallet,
  } = useQuery(['wallet'], async () => await getUserWallet(id))

  const {
    data: loyaltyBalance,
    isFetching: isLoyaltyFetching,
    refetch: refetchLoyalty,
  } = useQuery(['loyalty-balance', id], async () => await getUserLoyaltyBalance(id))

  const {
    data: loyaltyHistory,
    isFetching: isLoyaltyHistoryFetching,
  } = useQuery(
    ['loyalty-history', id, loyaltyHistoryPage, loyaltyHistoryPageSize],
    async () =>
      await getUserLoyaltyHistory(id, {
        currentPage: loyaltyHistoryPage,
        pageSize: loyaltyHistoryPageSize,
      }),
  )

  const {
    data: referralHistory,
    isFetching: isReferralHistoryFetching,
  } = useQuery(
    ['referral-history', id, referralHistoryPage, referralHistoryPageSize],
    async () =>
      await getUserReferralHistory(id, {
        currentPage: referralHistoryPage,
        pageSize: referralHistoryPageSize,
      }),
  )

  const updateLoyaltyStatusMutation = useMutation(
    (loyaltyEnabled) => updateUserLoyaltyStatus(id, loyaltyEnabled),
    {
      onSuccess: () => {
        message.success(t('customers.loyaltyStatusUpdated'))
        refetchLoyalty()
      },
      onError: () => {
        message.error(t('customers.loyaltyStatusUpdateFailed'))
      },
    }
  )

  const handleLoyaltyStatusChange = (checked) => {
    updateLoyaltyStatusMutation.mutate(checked)
  }

  const handleButtonClick = (buttonName) => {
    setActiveButton(buttonName === activeButton ? ACTIVE_BUTTON.ALL : buttonName)
    setTransactionType(buttonName)
  }

  const columns = [
    {
      title: '№',
      dataIndex: 'id',
      key: 'id',
      align: 'center',
      width: 57,
      render: (_, __, index) => index + 1 + pageSize * (currentPage - 1),
    },
    // {
    //   title: (props) => (
    //     <SortableColumnTitle {...props} title={t('wallet.table.order')} id="orderNumber" />
    //   ),
    //   dataIndex: 'orderNumber',
    //   key: 'orderNumber',
    //   sorter: true,
    //   render: (_, record) => {
    //     return <span>{record.orderNumber || '-'}</span>
    //   },
    // },
    // {
    //   title: t('wallet.table.orderAmount'),
    //   dataIndex: 'orderAmount',
    //   key: 'orderAmount',

    //   render: (_, record) => {
    //     const amount = record.orderAmount?.slice(0, -1)
    //     return <span>{record.orderAmount ? t('wallet.currency', { amount }) : '-'}</span>
    //   },
    // },
    {
      title: t('wallet.table.performedBy'),
      dataIndex: 'actorType',
      key: 'actorType',
      align: 'center',
      render: (_, record) => {
        return <span>{record.actorType === 'ADMIN' ? record.adminName : record.userName}</span>
      },
    },
    {
      title: t('wallet.table.type'),
      dataIndex: 'transactionType',
      key: 'transactionType',
      align: 'center',
      render: (_, record) => {
        // return <span>{record.transactionType === 'WITHDRAW' ? 'Outcome' : 'Income'}</span>
        return <span>{record.transactionType}</span>
      },
    },
    {
      title: t('wallet.table.amount'),
      dataIndex: 'transactionAmount',
      key: 'transactionAmount',
      align: 'center',
      render: (_, record) => {
        const amount = record.transactionAmount
        return <span>{t('wallet.currency', { amount })}</span>
      },
    },
    {
      title: 'Details',
      key: 'paymentMethod',
      render: (_, record) => {
        const paymentMethod = record.transactionType === 'WITHDRAW' ? 'WALLET': record.paymentMethod || 'MANUAL'
        const message = record.contextMessage || ''
        return (
          <div style={{ display: 'flex', flexDirection: 'column' }}>
            <span>{paymentMethod}</span>
            <span style={{ fontSize: '12px', color: 'gray' }}>{message}</span>
          </div>
        )
      },
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status) => <span>{status}</span>,
    },
    {
      title: (props) => (
        <SortableColumnTitle {...props} title={t('wallet.table.date')} id="createdAt" />
      ),
      dataIndex: 'createdAt',
      defaultSortOrder: 'descend',
      key: 'createdAt',
      render: (createdAt) => <span>{moment(createdAt).format('MM/DD/YYYY hh:mm:ss')}</span>,
      sorter: true,
      width: 200,
    },
  ]

  const ERROR_MESSAGES = {
    PHONE_NUMBER_ALREADY_TAKEN: () => t('errors.phoneNumberAlreadyTaken', 'Phone number already taken'),
  }
  
  const getErrorMessage = (code) =>
    ERROR_MESSAGES[code]?.() ?? code ?? t('errors.unknown', 'Something went wrong')
  
  const FIELD_MAP = {
    phoneNumber: 'mobile',
  }
  
  const handleServerErrors = (messages, formMethods) => {
    if (!messages?.length) return
  
    message.error(getErrorMessage(messages[0]?.message))
  
    if (!formMethods) return
  
    messages.forEach(({ property, message: code } = {}) => {
      if (!property) return
  
      formMethods.setError(FIELD_MAP[property] ?? property, {
        type: 'server',
        message: getErrorMessage(code),
      })
    })
  }
  

  const submitHandler = ({ id, ...data }, formMethods) => {
    dispatch(
      updateCustomer({
        id,
        data,
        navigate,
        onSuccess: () => {
          message.success(t('customers.updatedSuccessfully', 'Customer updated successfully'))
        },
        onError: (messages) => handleServerErrors(messages, formMethods),
      }),
    )
  }

  const handleWalletAmountEdit = () => {
    setIsModalVisible(true)
  }

  const handleWalletEditOnSubmit = async () => {
    setIsModalVisible(false)
    Promise.allSettled([refetchWallet(), refetchTransactions()])
  }

  const handleTableChange = (paginate, filter, sort) => {
    setCurrentPage(paginate.current)
    setPageSize(paginate.pageSize)
    setSortType(sort.columnKey)
    setSortOrder(sort.order)
  }

  const handleLoyaltyHistoryTableChange = (paginate) => {
    setLoyaltyHistoryPage(paginate.current)
    setLoyaltyHistoryPageSize(paginate.pageSize)
  }

  const handleReferralHistoryTableChange = (paginate) => {
    setReferralHistoryPage(paginate.current)
    setReferralHistoryPageSize(paginate.pageSize)
  }

  const handleSyncCustomerCode = () => {
    if (!id) return
    dispatch(
      syncCustomerSap({
        id,
      }),
    )
  }

  const walletSyncMutation = useMutation(() => syncUserWalletSap(id), {
    onSuccess: async (res) => {
      message.success(res?.message || 'Wallet sync queued')
      await refetchTransactions()
      await refetchWallet()
    },
    onError: (error) => {
      message.error(error?.response?.data?.message || 'Wallet sync failed')
    },
  })

  const handleSyncWallet = () => {
    if (!id) return
    walletSyncMutation.mutate()
  }

  const handleSyncBookletCoupons = () => {
    if (!id) return

    if (!customer?.users_sapId) {
      message.warning('Customer code is missing. Sync SAP customer code first.')
      return
    }

    dispatch(
      syncCustomerBookletCoupons({
        id,
        onSuccess: (res) => {
          const hasCounts =
            typeof res?.inserted === 'number' || typeof res?.updated === 'number'

          if (hasCounts) {
            const inserted = Number(res?.inserted || 0)
            const updated = Number(res?.updated || 0)
            const baseMessage = res?.message || 'Coupon sync completed'
            message.success(`${baseMessage} (${inserted} inserted, ${updated} updated)`)
            return
          }

          message.success(res?.message || 'Coupon sync queued')
        },
        onError: (messages, error) => {
          const firstMessage = messages?.[0]
          const normalizedMessage =
            (typeof firstMessage === 'string' && firstMessage) ||
            firstMessage?.message ||
            error?.response?.data?.message ||
            error?.message

          message.error(
            normalizedMessage || 'Coupon sync failed',
          )
        },
      }),
    )
  }

  useEffect(() => {
    dispatch(
      getCustomer({
        id,
        navigate,
      }),
    )
    return () => {
      // clear redux on unmount component
      dispatch(setCustomer({}))
    }
  }, [id, dispatch, navigate])

  return (
    <Page
      pageTitle={customer.users_fullName}
      isGetLoading={isGetLoading}
      isChangeLoading={isChangeLoading}
    >
      <CustomerForm
        defaultValues={{ ...customer, walletBalance: isWalletFetching ? '0' : wallet.balance }}
        submitHandler={submitHandler}
        loading={isChangeLoading || isWalletFetching}
        handleWalletAmountEdit={handleWalletAmountEdit}
        onSyncSap={handleSyncCustomerCode}
        sapSyncLoading={isChangeLoading}
        onSyncCoupons={handleSyncBookletCoupons}
        couponsSyncLoading={isChangeLoading}
        onSyncWallet={handleSyncWallet}
        walletSyncLoading={walletSyncMutation.isLoading}
      />
      <Divider />
      
      {/* Loyalty & Referral Section */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
        <Title level={3} style={{ marginBottom: 0 }}>{t('loyalty.customerSection.title')}</Title>
        {!isLoyaltyFetching && customer.users_id && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span>{t('customers.enableLoyalty')}</span>
            <Switch
              checked={customer.users_loyaltyEnabled !== false}
              onChange={handleLoyaltyStatusChange}
              loading={updateLoyaltyStatusMutation.isLoading}
            />
          </div>
        )}
      </div>
      {isLoyaltyFetching ? (
        <div style={{ textAlign: 'center', padding: '20px' }}>
          <Spin />
        </div>
      ) : loyaltyBalance ? (
        <Row gutter={16} style={{ marginBottom: 24 }}>
          <Col span={6}>
            <Card>
              <Statistic
                title={t('loyalty.customerSection.totalPoints')}
                value={loyaltyBalance.totalPoints || 0}
                valueStyle={{ color: '#3f8600' }}
              />
            </Card>
          </Col>
          <Col span={6}>
            <Card>
              <Statistic
                title={t('loyalty.customerSection.activePoints')}
                value={loyaltyBalance.activePoints || 0}
              />
            </Card>
          </Col>
          <Col span={6}>
            <Card>
              <Statistic
                title={t('loyalty.customerSection.redeemedPoints')}
                value={loyaltyBalance.redeemedPoints || 0}
                valueStyle={{ color: '#1890ff' }}
              />
            </Card>
          </Col>
          <Col span={6}>
            <Card>
              <Statistic
                title={t('loyalty.customerSection.referralCode')}
                value={loyaltyBalance.referralCode || '-'}
              />
              {loyaltyBalance.totalReferrals > 0 && (
                <div style={{ marginTop: 8, fontSize: '14px', color: '#666' }}>
                  {t('loyalty.customerSection.totalReferrals')}: {loyaltyBalance.totalReferrals}
                </div>
              )}
            </Card>
          </Col>
        </Row>
      ) : null}
      
      <Divider style={{ margin: '16px 0' }} />
      <Title level={3}>{t('wallet.table.header')}</Title>

      <Row className={classNames([styles.tableHeader])} justify="space-between">
        <Col span={5}>
          <Button
            className={classNames([
              styles.tableHeader__button,
              activeButton === ACTIVE_BUTTON.ALL && styles.tableHeader__button_active,
            ])}
            type="text"
            size="large"
            onClick={() => handleButtonClick(ACTIVE_BUTTON.ALL)}
          >
            {t('wallet.table.all')}
          </Button>
        </Col>
        <Col span={6}>
          <Button
            className={classNames([
              styles.tableHeader__button,
              activeButton === ACTIVE_BUTTON.DEPOSIT && styles.tableHeader__button_active,
            ])}
            type="text"
            size="large"
            onClick={() => handleButtonClick(ACTIVE_BUTTON.DEPOSIT)}
          >
            {t('wallet.table.income')}
          </Button>
        </Col>
        <Col span={8}>
          <Button
            className={classNames([
              styles.tableHeader__button,
              activeButton === ACTIVE_BUTTON.WITHDRAW && styles.tableHeader__button_active,
            ])}
            type="text"
            size="large"
            onClick={() => handleButtonClick(ACTIVE_BUTTON.WITHDRAW)}
          >
            {t('wallet.table.outcome')}
          </Button>
        </Col>
      </Row>
      <Table
        columns={columns}
        dataSource={isFetching || isError ? [] : transactions.items}
        rowKey={(record) => record.id}
        style={{ height: 'auto' }}
        onChange={handleTableChange}
        onRow={(record) => {
          return {
            onClick: () => {
              if (record.orderId) navigate(`/main/orders/view/${record.orderId}`)
            },
          }
        }}
        loading={isFetching}
        pagination={{
          pageSize,
          current: currentPage,
          showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items`,
          total: isFetching || isError ? 0 : transactions.total,
        }}
      />

      <Divider style={{ margin: '16px 0' }} />
      <Title level={3}>Loyalty History</Title>
      <Table
        columns={[
          {
            title: '№',
            key: 'index',
            align: 'center',
            width: 60,
            render: (_, __, index) =>
              index + 1 + loyaltyHistoryPageSize * (loyaltyHistoryPage - 1),
          },
          {
            title: 'Type',
            dataIndex: 'type',
            key: 'type',
            align: 'center',
          },
          {
            title: 'Points',
            dataIndex: 'points',
            key: 'points',
            align: 'center',
            render: (points) => (
              <span style={{ color: points < 0 ? '#cf1322' : '#3f8600' }}>
                {points}
              </span>
            ),
          },
          {
            title: 'Reason',
            dataIndex: 'reason',
            key: 'reason',
          },
          {
            title: 'Date',
            dataIndex: 'createdAt',
            key: 'createdAt',
            render: (createdAt) => <span>{moment(createdAt).format('MM/DD/YYYY hh:mm:ss')}</span>,
          },
        ]}
        dataSource={loyaltyHistory?.result || []}
        rowKey={(record, index) => `${record.createdAt}-${index}`}
        loading={isLoyaltyHistoryFetching}
        pagination={{
          pageSize: loyaltyHistoryPageSize,
          current: loyaltyHistoryPage,
          total: loyaltyHistory?.total || 0,
        }}
        onChange={handleLoyaltyHistoryTableChange}
        style={{ height: 'auto' }}
      />

      <Divider style={{ margin: '16px 0' }} />
      <Title level={3}>Referral History</Title>
      <Table
        columns={[
          {
            title: '№',
            key: 'index',
            align: 'center',
            width: 60,
            render: (_, __, index) =>
              index + 1 + referralHistoryPageSize * (referralHistoryPage - 1),
          },
          {
            title: 'Referrer',
            dataIndex: ['referrer', 'fullName'],
            key: 'referrer',
            render: (fullName, record) => (
              <div>
                <div>{fullName || '-'}</div>
                <div style={{ fontSize: '12px', color: '#888' }}>
                  {record.referrer?.phoneNumber || '-'}
                </div>
              </div>
            ),
          },
          {
            title: 'Referred',
            dataIndex: ['referredUser', 'fullName'],
            key: 'referred',
            render: (fullName, record) => (
              <div>
                <div>{fullName || '-'}</div>
                <div style={{ fontSize: '12px', color: '#888' }}>
                  {record.referredUser?.phoneNumber || '-'}
                </div>
              </div>
            ),
          },
          {
            title: 'Status',
            dataIndex: 'status',
            key: 'status',
          },
          {
            title: 'Referrer Points',
            dataIndex: 'referrerPoints',
            key: 'referrerPoints',
            align: 'center',
            render: (v) => v ?? '-',
          },
          {
            title: 'Referred Points',
            dataIndex: 'referredPoints',
            key: 'referredPoints',
            align: 'center',
            render: (v) => v ?? '-',
          },
          {
            title: 'Date',
            dataIndex: 'createdAt',
            key: 'createdAt',
            render: (createdAt) => <span>{moment(createdAt).format('MM/DD/YYYY hh:mm:ss')}</span>,
          },
        ]}
        dataSource={referralHistory?.result || []}
        rowKey={(record) => record.id}
        loading={isReferralHistoryFetching}
        pagination={{
          pageSize: referralHistoryPageSize,
          current: referralHistoryPage,
          total: referralHistory?.total || 0,
        }}
        onChange={handleReferralHistoryTableChange}
        style={{ height: 'auto' }}
      />
      <EditWalletModal
        isVisible={isModalVisible}
        onSubmit={handleWalletEditOnSubmit}
        onClose={() => setIsModalVisible(false)}
        walletAmount={isWalletFetching ? 0 : wallet.balance}
        userId={id}
      />
    </Page>
  )
}

export default CustomersView
